import type { Colleague, PartnerRecord, PartnerRow } from "@api/generated/model";
import {
  getPartnersExportUrl,
  getPartnersLabelsUrl,
  usePartnersCountries,
  usePartnersPartners,
  usePartnersRecord,
  usePartnersSave,
} from "@api/generated/partners/partners";
import { useUserMe } from "@api/generated/user/user";
import { hasRole } from "@app/workspace";
import "@features/genebank/genebank.css";
import {
  FIELDS,
  NewPartnerForm,
  PartnerFields,
  partnerRequirements,
  REQUIRED,
  useInvalidatePartners,
  type Values,
} from "@features/genebank/PartnerForm";
import { hint } from "@i18n";
import { Button, Checkbox, Loader, SegmentedControl, Select, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { formatDate, formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { MissingList } from "@ui/RunBar";
import { Splitter } from "@ui/Splitter";
import { sortText } from "@ui/sort";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/genebank/partners")({
  validateSearch: z.object({
    q: z.string().optional(),
    country: z.string().optional(),
    inactive: z.boolean().optional(),
    caz: z.union([z.number().int(), z.literal("new")]).optional(),
    from: z.number().int().optional(), // a new partner at the institution of this one (a colleague)
    field: z.string().optional(), // a field holding ``value`` (the legacy Find like)
    value: z.string().optional(),
    match: z.enum(["contains", "exact"]).optional(),
  }),
  component: PartnersPage,
});

// the server's field a column orders the address labels and the Excel file by, where it is not the column's id
const SERVER_ORDER: Record<string, string> = { contact: "contact_name" };

/** Génbank és magposta → Partnerek (the legacy Cimlista): the institutions and contacts of the seed exchange. */
function PartnersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canEdit = hasRole(useUserMe().data?.roles, "genbank");
  const [asked] = useDebouncedValue((search.q ?? "").trim(), 300);
  const [value] = useDebouncedValue((search.value ?? "").trim(), 300);
  const params = {
    q: asked,
    country: search.country,
    inactive: search.inactive,
    ...(search.field && value ? { field: search.field, value, match: search.match ?? "contains" } : {}),
  };
  // the whole address list (the server's limit is far above it): the table sorts and filters it
  const list = usePartnersPartners(params, { query: { placeholderData: keepPreviousData } });
  const columns = useMemo<Column<PartnerRow>[]>(
    () => [
      { id: "caz", header: "CAz", value: (p) => p.caz, numeric: true, text: (p) => String(p.caz), className: "mono" },
      { id: "country_abbr", header: t("partners.country"), value: (p) => p.country_abbr ?? p.country, className: "mono" },
      { id: "city", header: t("partners.field.city"), value: (p) => p.city },
      { id: "institution", header: t("partners.field.institution"), value: (p) => p.institution, className: "wrap" },
      { id: "contact", header: t("partners.contact"), value: (p) => p.contact },
      {
        id: "shipments",
        header: t("partners.shipmentsAbbr"),
        value: (p) => p.shipments,
        numeric: true,
        // none sent: blank, the list reads easier
        text: (p) => (p.shipments ? formatNumber(p.shipments) : ""),
      },
    ],
    [t],
  );
  const table = useDataTable("genebank.partners", columns, list.data);
  // the address labels and the Excel file in the order on show (the server's default order without one)
  const printed = table.sort.length
    ? { ...params, sort: table.sort.map((k) => sortText({ ...k, field: SERVER_ORDER[k.field] ?? k.field })) }
    : params;
  const countries = usePartnersCountries({ query: { staleTime: 5 * 60_000 } }).data;
  // the search is in the link (replaced, not a history entry per keystroke)
  const set = (patch: Partial<typeof search>) =>
    void navigate({ search: (s) => ({ ...s, ...patch }), replace: "q" in patch || "value" in patch });
  const fields = [{ value: "caz", label: "CAz" }, ...FIELDS.map((f) => ({ value: f, label: t(`partners.field.${f}`) }))];
  const shown = typeof search.caz === "number" ? search.caz : undefined;
  return (
    <div className="page fill">
      <PageHeader
        title={t("partners.title")}
        subtitle={t("partners.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            <Button component="a" size="compact-sm" variant="default" href={getPartnersLabelsUrl(printed)}>
              {t("partners.printAddressList")}
            </Button>
            <Button component="a" size="compact-sm" variant="default" href={getPartnersExportUrl(printed)}>
              {t("partners.excel")}
            </Button>
            {canEdit && (
              <Button size="compact-sm" onClick={() => set({ caz: "new" })}>
                {t("partners.newPartner")}
              </Button>
            )}
          </div>
        }
      />
      <div className="fill-row">
        <section className="card stack pt-list">
          <div className="hstack inset" style={{ gap: 8, paddingBottom: 0 }}>
            <TextInput
              className="grow"
              size="xs"
              placeholder={t("partners.search")}
              aria-label={t("partners.search")}
              value={search.q ?? ""}
              onChange={(e) => set({ q: e.currentTarget.value || undefined })}
            />
            <Select
              size="xs"
              w={110}
              placeholder={t("partners.country")}
              aria-label={t("partners.country")}
              clearable
              searchable
              value={search.country ?? null}
              onChange={(v) => set({ country: v ?? undefined })}
              data={countries?.codes ?? []}
            />
            <Checkbox
              size="xs"
              label={t("partners.inactive")}
              checked={!!search.inactive}
              onChange={(e) => set({ inactive: e.currentTarget.checked || undefined })}
            />
          </div>
          <div className="hstack inset" style={{ gap: 8, paddingBottom: 0, alignItems: "flex-end" }}>
            <Select
              size="xs"
              w={130}
              label={t("partners.searchField")}
              placeholder={hint("searchSelect")}
              searchable
              clearable
              value={search.field ?? null}
              onChange={(v) => set({ field: v ?? undefined })}
              data={fields}
            />
            <TextInput
              className="grow"
              size="xs"
              label={t("partners.searchValue")}
              disabled={!search.field}
              value={search.value ?? ""}
              onChange={(e) => set({ value: e.currentTarget.value || undefined })}
            />
            <SegmentedControl
              size="xs"
              value={search.match ?? "contains"}
              onChange={(v) => set({ match: v as "contains" | "exact" })}
              data={(["contains", "exact"] as const).map((m) => ({ value: m, label: t(`partners.match.${m}`) }))}
            />
          </div>
          <DataTable
            table={table}
            rowKey={(p) => p.caz}
            className="grow"
            rowProps={(p) => ({
              className: `click${p.caz === shown ? " sel" : ""}${p.active ? "" : " mu"}`,
              onClick: () => set({ caz: p.caz }),
            })}
          />
          <div className="sm mu inset hstack" style={{ gap: 8 }}>
            <span className="grow">
              {countries ? (
                t("partners.total", { n: formatNumber(countries.active_total), countries: formatNumber(countries.codes.length) })
              ) : (
                <Loader size="xs" />
              )}
            </span>
          </div>
        </section>
        <Splitter cssVar="--pt-list-w" side="start" min={360} max={900} />
        <div className="grow stack" style={{ gap: 16, minWidth: 0, overflowY: "auto" }}>
          {search.caz === "new" && canEdit ? (
            <NewPartner key={search.from ?? "new"} from={search.from} onDone={(caz) => set({ caz, from: undefined })} />
          ) : shown != null ? (
            <Partner
              key={shown}
              caz={shown}
              canEdit={canEdit}
              onColleague={() => set({ caz: "new", from: shown })}
              onOpen={(caz) => set({ caz })}
            />
          ) : (
            <section className="card pad sm mu">{t("partners.selectPrompt")}</section>
          )}
        </div>
      </div>
    </div>
  );
}

type PartnerProps = { canEdit: boolean; onColleague: () => void; onOpen: (caz: number) => void };

function Partner({ caz, ...props }: PartnerProps & { caz: number }) {
  const record = usePartnersRecord(caz).data;
  if (!record) return <Loader size="sm" />;
  return <PartnerForm record={record} {...props} />;
}

/** The other contact persons at the partner's institution (same institution and country), each opened by a click,
 *  and the button that adds one. */
function Colleagues(props: { colleagues: Colleague[]; canAdd: boolean; onAdd: () => void; onOpen: (caz: number) => void }) {
  const { t } = useTranslation();
  return (
    <section className="card pad stack" style={{ gap: 6 }}>
      <div className="hstack" style={{ gap: 10 }}>
        <h2 className="h2 grow">{t("partners.colleagues")}</h2>
        {props.canAdd && (
          <Button size="compact-sm" variant="default" style={{ flexShrink: 0 }} onClick={props.onAdd}>
            {t("partners.colleague")}
          </Button>
        )}
      </div>
      {props.colleagues.length ? (
        props.colleagues.map((c) => (
          <button
            type="button"
            key={c.caz}
            className={`pt-history pt-colleague${c.active ? "" : " mu"}`}
            onClick={() => props.onOpen(c.caz)}
          >
            <span className="grow">
              <strong>{c.contact || `CAz ${c.caz}`}</strong>
              {c.position && <span className="mu"> · {c.position}</span>}
              {!c.active && (
                <span className="chip" style={{ marginLeft: 8 }}>
                  {t("partners.inactive")}
                </span>
              )}
            </span>
            <span className="sm mu">{c.email}</span>
          </button>
        ))
      ) : (
        <span className="sm mu">{t("partners.noColleagues")}</span>
      )}
    </section>
  );
}

function PartnerForm({ record, canEdit, onColleague, onOpen }: PartnerProps & { record: PartnerRecord }) {
  const { t } = useTranslation();
  const invalidate = useInvalidatePartners();
  const [values, setValues] = useState<Values>(record.values);
  const [active, setActive] = useState(record.active);
  const dirty = active !== record.active || FIELDS.some((f) => (values[f] ?? "") !== (record.values[f] ?? ""));
  // a migrated partner may lack the required country code: it is shown as it is, saving it asks for it
  const missing = REQUIRED.some((f) => !record.values[f]?.trim());
  const requirements = partnerRequirements(t, values, "partner");
  // another partner, another page or closing the tab with unsaved changes asks first
  // (a search typed in the list is a navigation too: only leaving this partner counts)
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      dirty &&
      !save.isPending &&
      (next.pathname !== current.pathname || (next.search as { caz?: number }).caz !== (current.search as { caz?: number }).caz),
    withResolver: true,
    enableBeforeUnload: () => dirty,
  });
  const save = usePartnersSave({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("partners.saved") });
        invalidate();
      },
    },
  });
  return (
    <>
      <section className="card pad stack" style={{ gap: 12 }}>
        <div className="hstack" style={{ gap: 10 }}>
          <h2 className="h2 grow">{values.institution || t("partners.unnamed")}</h2>
          <span className="chip">CAz {record.caz}</span>
        </div>
        {canEdit && missing && <div className="note warn sm">{t("partners.fillCountryCode")}</div>}
        <PartnerFields values={values} set={(p) => setValues((v) => ({ ...v, ...p }))} readOnly={!canEdit} idPrefix="partner" />
        {canEdit && dirty && <MissingList requirements={requirements} />}
        <div className="hstack" style={{ gap: 8 }}>
          <Checkbox
            className="grow"
            label={t("partners.active")}
            checked={active}
            disabled={!canEdit}
            onChange={(e) => setActive(e.currentTarget.checked)}
          />
          {canEdit && (
            <>
              <Button
                variant="default"
                disabled={!dirty}
                onClick={() => {
                  setValues(record.values);
                  setActive(record.active);
                }}
              >
                {t("partners.discard")}
              </Button>
              <Button
                disabled={!dirty || requirements.some((r) => !r.done)}
                loading={save.isPending}
                onClick={() => save.mutate({ caz: record.caz, data: { ...values, active, token: record.token } })}
              >
                {t("partners.save")}
              </Button>
            </>
          )}
        </div>
      </section>
      {blocker.status === "blocked" && (
        <ConfirmModal
          title={t("partners.discardTitle")}
          text={t("partners.discardConfirm", { name: record.values.institution || t("partners.unnamed") })}
          confirm={t("partners.discardContinue")}
          danger
          onConfirm={blocker.proceed}
          onClose={blocker.reset}
        />
      )}
      {record.values.institution?.trim() && (
        <Colleagues colleagues={record.colleagues} canAdd={canEdit} onAdd={onColleague} onOpen={onOpen} />
      )}
      <section className="card pad stack" style={{ gap: 6 }}>
        <h2 className="h2">{t("partners.history")}</h2>
        {record.history.length ? (
          record.history.map((x) => (
            <div key={`${x.kind}-${x.id}`} className="pt-history">
              <span className={`chip ${x.kind === "shipment" ? "b" : "g"}`}>{t(`partners.${x.kind}`)}</span>
              <span className="grow">
                {x.kind === "shipment"
                  ? t("partners.shipmentRow", { id: x.id, n: x.items, kg: formatNumber(x.kg) })
                  : t("partners.arrivalRow", { id: x.id, n: x.items })}
              </span>
              <span className="sm mu">{formatDate(x.day)}</span>
            </div>
          ))
        ) : (
          <span className="sm mu">{t("partners.noHistory")}</span>
        )}
      </section>
    </>
  );
}

/** A new partner; ``from``: a colleague at that partner's institution (its institution and address kept). */
function NewPartner({ from, onDone }: { from: number | undefined; onDone: (caz: number) => void }) {
  const { t } = useTranslation();
  const source = usePartnersRecord(from ?? 0, { query: { enabled: from != null } }).data;
  if (from != null && !source) return <Loader size="sm" />;
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <h2 className="h2">{source ? t("partners.colleagueTitle") : t("partners.newTitle")}</h2>
      <NewPartnerForm kept={source?.values} onDone={onDone} />
    </section>
  );
}
